import {useEffect, useState} from 'react';

import {isPositive} from 'ramda-adjunct';

import {
  GetServiceCaseOrdersApiResponse,
  GetServiceOrderApiResponse,
  useLazyGetServiceOrderQuery,
} from '@dms/api/workshop';

interface OrderWithMarginUnderMinimum {
  number: string;
  serviceOrderId: string;
}

interface UseOrdersWithMarginUnderMinimumProps {
  serviceCaseId: string;
  serviceCaseOrders: GetServiceCaseOrdersApiResponse | undefined;
}

export function useOrdersWithMarginUnderMinimum(
  props: UseOrdersWithMarginUnderMinimumProps
): OrderWithMarginUnderMinimum[] {
  const [ordersWithMarginUnderMinimum, setOrdersWithMarginUnderMinimum] = useState<
    OrderWithMarginUnderMinimum[]
  >([]);
  const [getServiceOrder] = useLazyGetServiceOrderQuery();

  useEffect(() => {
    if (!props.serviceCaseOrders?.length) {
      return;
    }

    const subscriptions = props.serviceCaseOrders.map((order) =>
      getServiceOrder({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: order.serviceOrderId,
      })
    );

    Promise.all(subscriptions.map((sub) => sub.unwrap()))
      .then((results: (GetServiceOrderApiResponse | undefined)[]) => {
        const filtered = results
          .filter(
            (result): result is NonNullable<GetServiceOrderApiResponse> =>
              !!result && isPositive(result.serviceItems?.marginUnderMinimumCount)
          )
          .map((result) => ({
            number: result.number ?? '',
            serviceOrderId: result.id,
          }));
        setOrdersWithMarginUnderMinimum(filtered);
      })
      .catch(() => {
        setOrdersWithMarginUnderMinimum([]);
      });

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe());
    };
  }, [props.serviceCaseOrders, props.serviceCaseId, getServiceOrder]);

  return props.serviceCaseOrders?.length ? ordersWithMarginUnderMinimum : [];
}
